/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

export type TooltipManagerCallback = () => void;

const FocusTriggerKeys = ['Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];

/**
 * A global tooltip manager that
 * - ensures that only one tooltip is shown at a time
 * - detects a Tab key press and allows tooltips to check if their target was focused via keyboard
 */
export class TooltipManager {
  private activeCallback: TooltipManagerCallback | null;

  private triggerKeyActive: boolean;

  private keyDownHandler = (event: KeyboardEvent) => {
    if (FocusTriggerKeys.includes(event.key)) {
      this.triggerKeyActive = true;
      setTimeout(() => {
        this.triggerKeyActive = false;
      }, 100);
    }
    if (this.activeCallback && event.key === 'Escape') {
      // hide active tooltip on Escape
      this.activeCallback();
    }
  };

  constructor() {
    this.activeCallback = null;
    this.triggerKeyActive = false;
    window.addEventListener('keydown', this.keyDownHandler, true);
  }

  /**
   * notifies the previously registered active tooltip callback and registers a new one
   */
  public register = (callback: TooltipManagerCallback) => {
    if (this.activeCallback && this.activeCallback !== callback) {
      this.activeCallback();
    }
    this.activeCallback = callback;
  };

  /**
   * resets the registered active callback
   */
  public unregister = (callback: TooltipManagerCallback) => {
    if (this.activeCallback === callback) {
      this.activeCallback = null;
    }
  };

  /**
   * returns the active Tab key status ('active' means a Tab key press within the last 100ms)
   */
  public isTabKeyActive = () => {
    return this.triggerKeyActive;
  };
}
